/** Editor i18n catalog: billing (spec #304). English is the key source; German uses "du" and real umlauts. */
import { defineMessages } from "../core.ts";

export const billing = defineMessages(
  {
    // Shared by the pay screen and billing details.
    back: "Dashboard",
    loading: "Loading…",
    cancel: "Cancel",

    // EditorBilling (pay).
    title: "Billing",
    period_label: "Billing period",
    err_unavailable: "Billing isn't available right now - try again shortly.",
    err_load: "Could not load your pay for this period.",
    status_live: "Running total for the current period - nothing is final yet.",
    status_legacy: "Pre-cutover month, settled through the old system - read-only.",
    needs_rate_warning: "A card here still needs a rate before this invoice can be approved.",
    col_card: "Card",
    col_amount: "Amount",
    needs_rate_pill: "needs a rate",
    appeal: "Appeal",
    no_cards: "No cards counted this period.",
    line_delivery: "Card",
    line_needs_rate: "Needs a rate",
    line_mentor: "Mentor bonus",
    line_late: "Late",
    line_gen_own_cost: "gen Own Cost",
    line_adjustment: "Adjustment",
    total: "Total",
    invoice_title: "Invoice {period}",
    net_earnings: "Net earnings",
    cards_one: "1 card",
    cards_many: "{n} cards",
    badge_live: "Running",
    badge_draft: "Draft",
    badge_approved: "Approved",
    badge_paid: "Paid",
    badge_legacy: "Archived",
    appeal_whole: "Appeal whole invoice",
    marking: "Marking…",
    mark_sent: "I've sent my invoice",
    invoice_sent: "Invoice sent - claimed total {amount}",
    err_mark: "Could not mark that invoice sent - try again.",
    your_claims: "Your claims",
    claim_open: "open",
    claim_accepted: "accepted",
    claim_rejected: "rejected",
    whole_invoice: "the whole invoice",
    appeal_target: "Appeal {target}",
    claim_text_ph: "Why should this be reviewed?",
    proof_ph: "Proof link (Loom, screenshot or FreeFrame)",
    proof_hint: "A claim needs a link to your proof, starting with https://",
    filing: "Filing…",
    file_claim: "File claim",
    err_claim: "Could not file that claim - try again.",

    // BillingDetails (where to pay the editor).
    details_title: "Billing details",
    details_intro: "Used to pay you. Visible in full to Aditor payroll only.",
    f_legal_name: "Legal name",
    f_account_holder: "Account holder",
    f_address1: "Address line 1",
    f_address2: "Address line 2",
    f_postal_code: "Postal code",
    f_city: "City",
    f_country: "Country",
    f_bic: "SWIFT/BIC",
    f_tax_number: "Tax number",
    f_vat_id: "VAT ID",
    f_vat_status: "VAT status",
    field_optional: "{label} (optional)",
    iban_change: "Change",
    vat_small_business: "Small business (no VAT charged)",
    vat_registered: "VAT registered",
    vat_none: "Not applicable",
    not_set: "Not set",
    saving: "Saving…",
    save: "Save",
    saved: "Saved.",
    err_save: "Could not save your billing details - try again.",
    err_invalid_iban_server: "That doesn't look like a valid IBAN.",
    err_missing_required: "Fill in every required field.",
    req_legal_name: "Legal name is required",
    req_address: "Address is required",
    req_postal_code: "Postal code is required",
    req_city: "City is required",
    req_country: "Country is required",
    req_account_holder: "Account holder is required",
    req_iban: "IBAN is required",
    invalid_iban: "That doesn't look like a valid IBAN",
    f_payout_method: "How we pay you",
    method_iban: "IBAN",
    method_bank_account: "Bank account without IBAN",
    method_bank_account_hint: "For countries without IBAN. Use the bank name and account number your bank gives for international transfers. Add the SWIFT/BIC if your bank has one.",
    f_bank_name: "Bank name",
    f_account_number: "Account number",
    req_bank_name: "Bank name is required",
    req_account_number: "Account number is required",
    invalid_bank_name: "That doesn't look like a bank name",
    invalid_account_number: "That doesn't look like an account number (4 to 34 letters or digits)",
    account_number_is_iban: "That is an IBAN. Choose IBAN above instead.",
    invalid_bic: "That doesn't look like a SWIFT/BIC code (8 or 11 characters)",
    prompt_title: "Add your billing details",
    prompt_body: "We need your legal name, address and bank details (IBAN, or account number where there is no IBAN) to pay you. Takes a minute - you can change it any time.",
    prompt_fill: "Fill in now",
    prompt_later: "Later",
  },
  {
    back: "Dashboard",
    loading: "Wird geladen…",
    cancel: "Abbrechen",

    title: "Abrechnung",
    period_label: "Abrechnungszeitraum",
    err_unavailable: "Die Abrechnung ist gerade nicht erreichbar - versuch es gleich nochmal.",
    err_load: "Deine Vergütung für diesen Zeitraum konnte nicht geladen werden.",
    status_live: "Laufende Summe für den aktuellen Zeitraum - noch ist nichts final.",
    status_legacy: "Monat vor der Umstellung, über das alte System abgerechnet - nur lesbar.",
    needs_rate_warning: "Eine Card hier braucht noch einen Satz, bevor diese Rechnung freigegeben werden kann.",
    col_card: "Cards",
    col_amount: "Betrag",
    needs_rate_pill: "braucht einen Satz",
    appeal: "Einspruch",
    no_cards: "In diesem Zeitraum wurden keine Cards gezählt.",
    line_delivery: "Gelieferte Card",
    line_needs_rate: "Braucht einen Satz",
    line_mentor: "Mentor-Bonus",
    line_late: "Verspätung",
    line_gen_own_cost: "gen-Eigenkosten",
    line_adjustment: "Anpassung",
    total: "Gesamt",
    invoice_title: "Rechnung {period}",
    net_earnings: "Netto-Verdienst",
    cards_one: "1 Card",
    cards_many: "{n} Cards",
    badge_live: "Läuft",
    badge_draft: "Entwurf",
    badge_approved: "Freigegeben",
    badge_paid: "Bezahlt",
    badge_legacy: "Archiviert",
    appeal_whole: "Einspruch gegen die ganze Rechnung",
    marking: "Wird markiert…",
    mark_sent: "Ich habe meine Rechnung gesendet",
    invoice_sent: "Rechnung gesendet - angegebene Summe {amount}",
    err_mark: "Die Rechnung konnte nicht als gesendet markiert werden - versuch es nochmal.",
    your_claims: "Deine Einsprüche",
    claim_open: "offen",
    claim_accepted: "angenommen",
    claim_rejected: "abgelehnt",
    whole_invoice: "die ganze Rechnung",
    appeal_target: "Einspruch: {target}",
    claim_text_ph: "Was soll geprüft werden und warum?",
    proof_ph: "Beweis-Link (Loom, Screenshot oder FreeFrame)",
    proof_hint: "Ein Einspruch braucht einen Link zu deinem Beweis, beginnend mit https://",
    filing: "Wird eingereicht…",
    file_claim: "Einspruch einreichen",
    err_claim: "Der Einspruch konnte nicht eingereicht werden - versuch es nochmal.",

    details_title: "Rechnungsdaten",
    details_intro: "Damit wir dich bezahlen können. Vollständig sichtbar nur für die Aditor-Lohnabrechnung.",
    f_legal_name: "Vollständiger Name",
    f_account_holder: "Kontoinhaber",
    f_address1: "Adresszeile 1",
    f_address2: "Adresszeile 2",
    f_postal_code: "Postleitzahl",
    f_city: "Stadt",
    f_country: "Land",
    f_bic: "SWIFT/BIC",
    f_tax_number: "Steuernummer",
    f_vat_id: "USt-IdNr.",
    f_vat_status: "Umsatzsteuerstatus",
    field_optional: "{label} (falls vorhanden)",
    iban_change: "Ändern",
    vat_small_business: "Kleinunternehmer (keine Umsatzsteuer)",
    vat_registered: "Umsatzsteuerpflichtig",
    vat_none: "Nicht zutreffend",
    not_set: "Nicht festgelegt",
    saving: "Wird gespeichert…",
    save: "Speichern",
    saved: "Gespeichert.",
    err_save: "Deine Rechnungsdaten konnten nicht gespeichert werden - versuch es nochmal.",
    err_invalid_iban_server: "Das sieht nicht nach einer gültigen IBAN aus.",
    err_missing_required: "Füll alle Pflichtfelder aus.",
    req_legal_name: "Bitte gib deinen vollständigen Namen an",
    req_address: "Bitte gib deine Adresse an",
    req_postal_code: "Bitte gib deine Postleitzahl an",
    req_city: "Bitte gib deine Stadt an",
    req_country: "Bitte gib dein Land an",
    req_account_holder: "Bitte gib den Kontoinhaber an",
    req_iban: "Bitte gib deine IBAN an",
    invalid_iban: "Das sieht nicht nach einer gültigen IBAN aus",
    f_payout_method: "Wie wir dich bezahlen",
    method_iban: "IBAN",
    method_bank_account: "Bankkonto ohne IBAN",
    method_bank_account_hint: "Für Länder ohne IBAN. Trag den Banknamen und die Kontonummer ein, die deine Bank für Auslandsüberweisungen angibt. Füg den SWIFT/BIC hinzu, wenn deine Bank einen hat.",
    f_bank_name: "Name der Bank",
    f_account_number: "Kontonummer",
    req_bank_name: "Bitte gib den Namen deiner Bank an",
    req_account_number: "Bitte gib deine Kontonummer an",
    invalid_bank_name: "Das sieht nicht nach einem Banknamen aus",
    invalid_account_number: "Das sieht nicht nach einer Kontonummer aus (4 bis 34 Buchstaben oder Ziffern)",
    account_number_is_iban: "Das ist eine IBAN. Wähl oben IBAN aus.",
    invalid_bic: "Das sieht nicht nach einem SWIFT/BIC-Code aus (8 oder 11 Zeichen)",
    prompt_title: "Trag deine Rechnungsdaten ein",
    prompt_body: "Wir brauchen deinen vollständigen Namen, deine Adresse und deine Bankverbindung (IBAN, oder Kontonummer, wo es keine IBAN gibt), um dich zu bezahlen. Dauert eine Minute - du kannst alles jederzeit ändern.",
    prompt_fill: "Jetzt ausfüllen",
    prompt_later: "Später",
  },
);
